import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AccountCard } from "../components/profile/AccountCard";
import { ApiKey } from "../redux/reducers/userSlice";
import { ApiKeyList } from "../components/profile/ApiKeyList";
import { InvitesCard } from "../components/profile/InvitesCard";
import { OrganizationsCard } from "../components/profile/OrganizationsCard";
import { Button, InputField, SettingsList, Text, View } from "../components/base";
import { Dialog } from "../components/base/Dialog";
import { Icon } from "../components/base/Icon";
import { PageLayout } from "../components/PageLayout";
import { OrganizationSettingsTab } from "../components/profile/OrganizationSettingTab";
import { useApiRequest } from "../hooks/useApiRequest";
import { useAppSelector } from "../redux";
import { useNavigation } from "../hooks/useNavigation";

export const ProfilePage = () => {
  const user = useAppSelector((state) => state.session.user);
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [description, setDescription] = useState("");
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const createApiKeyRequest = useApiRequest<{ data: ApiKey }>();
  const getApiKeysRequest = useApiRequest<{ data: ApiKey[] }>();
  const revokeApiKeyRequest = useApiRequest();

  const onClickCreateApiKey = async () => {
    const response = await createApiKeyRequest.fetch("/v1/me/personal-access-tokens", {
      method: "POST",
      body: { description },
    });

    if (response?.data) {
      setNewApiKey(response.data?.data?.token || "");
      setShowApiKeyDialog(true);
      setShowDescriptionDialog(false);
      setDescription("");
      getApiKeysRequest.fetch("/v1/me/personal-access-tokens");
    }
  };

  const onClickDeleteApiKey = async (keyId: string) => {
    await revokeApiKeyRequest.fetch(`/v1/me/personal-access-tokens/${keyId}`, {
      method: "DELETE",
    });
    getApiKeysRequest.fetch("/v1/me/personal-access-tokens");
  };

  const onClickCopyApiKey = async () => {
    await navigator.clipboard.writeText(newApiKey);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 1500);
  };

  useEffect(() => {
    getApiKeysRequest.fetch("/v1/me/personal-access-tokens");
  }, []);

  return (
    <PageLayout
      title="Profile"
      description="Manage your account settings"
      headerAction={
        <Button
          variant="danger"
          size="sm"
          outline
          onClick={() => navigation.navigate("/signout")}
        >
          <Icon
            name="logout"
            size={14}
          />
          Sign out
        </Button>
      }
    >
      <View className="flex flex-col lg:flex-row gap-6 mt-6">
        {/* Left column - 2/3 */}
        <View className="flex flex-col gap-6 flex-1 lg:w-2/3 min-w-0">
          <AccountCard
            userId={user?.id}
            email={user?.email}
            name={user?.name}
          />
          <OrganizationsCard organizations={user?.organizations ?? []} />
          <InvitesCard />
        </View>

        {/* Right column - 1/3 */}
        <View className="flex flex-col gap-6 w-full lg:w-1/3 shrink-0">
          {/*
            <OrganizationSettingsTab title="Notifications">
              <SettingsList>
                <button
                  onClick={() => navigate("/notification-preferences")}
                  className="flex flex-row items-center gap-3 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                >
                  <View className="flex-shrink-0 h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <Icon
                      name="notification"
                      size={14}
                      className="text-gray-600 dark:text-gray-300"
                    />
                  </View>
                  <View className="flex flex-col min-w-0 flex-1">
                    <Text
                      variant="body"
                      className="font-semibold"
                    >
                      Notification Preferences
                    </Text>
                    <Text
                      variant="muted"
                      className="text-sm text-gray-500 dark:text-gray-400"
                    >
                      Manage your notification settings
                    </Text>
                  </View>
                  <Icon
                    name="chevron-right"
                    size={14}
                    className="text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors shrink-0"
                  />
                </button>
              </SettingsList>
            </OrganizationSettingsTab>
          */}
          <ApiKeyList
            apiKeys={getApiKeysRequest.data?.data || []}
            onCreateKey={() => setShowDescriptionDialog(true)}
            onDeleteKey={onClickDeleteApiKey}
            isLoading={getApiKeysRequest.isLoading}
          />
        </View>
      </View>

      <Dialog
        isOpen={showDescriptionDialog}
        onClose={() => setShowDescriptionDialog(false)}
        title="New Personal Access Token"
      >
        <View className="p-4 space-y-4">
          <Text variant="body">Enter a description for your new personal access token:</Text>
          <InputField
            value={description}
            onChange={(description) => setDescription(description)}
            placeholder="Enter token description..."
            className="w-full text-slate-700"
          />
          <Button
            onClick={onClickCreateApiKey}
            isDisabled={!description || createApiKeyRequest.isLoading}
            className="bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white px-4 py-2 w-full"
          >
            {createApiKeyRequest.isLoading ? "Generating..." : "Generate Token"}
          </Button>
        </View>
      </Dialog>

      <Dialog
        isOpen={showApiKeyDialog}
        onClose={() => setShowApiKeyDialog(false)}
        title="Your New Personal Access Token"
      >
        <View className="flex flex-col p-4">
          <Text
            variant="body"
            className="mb-4"
          >
            Please save this token - you won't be able to see it again!
          </Text>
          <View className="flex items-center space-x-2">
            <InputField
              value={newApiKey}
              readOnly
              className="flex-1"
            />
            <Button
              onClick={onClickCopyApiKey}
              variant="brand"
              className="whitespace-nowrap"
            >
              {showCopySuccess ? "Copied!" : "Copy Token"}
            </Button>
          </View>
        </View>
      </Dialog>
    </PageLayout>
  );
};
